import { GAME_STATUS, ACTIONS, CANVAS_CONFIG } from './constants.js';
import { generateHorizontalLines, calculatePath } from './ladderGenerator.js';

/**
 * Initialize vertical lines equally spaced
 */
function initializeVerticalLines(playerCount) {
  const verticalLines = [];
  const usableWidth = CANVAS_CONFIG.width - CANVAS_CONFIG.padding * 2;
  const spacing = usableWidth / (playerCount - 1);

  for (let i = 0; i < playerCount; i++) {
    verticalLines.push({
      x: CANVAS_CONFIG.padding + i * spacing,
      columnIndex: i,
    });
  }

  return verticalLines;
}

/**
 * Shuffle rewards array so that the predetermined winner ends up at the result position
 * @param {Array} rewards - Original rewards array
 * @param {number} predeterminedWinnerIndex - Index of the predetermined winner in original array
 * @param {number} targetPosition - Position where the winner should end up
 * @returns {Array} Shuffled rewards array
 */
function shuffleRewardsWithConstraint(rewards, predeterminedWinnerIndex, targetPosition) {
  // Extract the predetermined winner
  const winner = rewards[predeterminedWinnerIndex];

  // Get all other rewards
  const otherRewards = rewards.filter((_, index) => index !== predeterminedWinnerIndex);

  // Fisher-Yates shuffle for other rewards
  for (let i = otherRewards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [otherRewards[i], otherRewards[j]] = [otherRewards[j], otherRewards[i]];
  }

  // Insert winner at target position
  const shuffledRewards = [...otherRewards];
  shuffledRewards.splice(targetPosition, 0, winner);

  return shuffledRewards;
}

/**
 * Ladder reducer function
 */
export function ladderReducer(state, action) {
  switch (action.type) {
    case ACTIONS.INITIALIZE_GAME: {
      const verticalLines = initializeVerticalLines(state.config.playerCount);

      return {
        ...state,
        status: GAME_STATUS.SELECTING,
        ladderData: {
          ...state.ladderData,
          verticalLines,
        },
      };
    }

    case ACTIONS.SELECT_START: {
      return {
        ...state,
        selectedStartIndex: action.payload.startIndex,
      };
    }

    case ACTIONS.START_GAME: {
      const { rewards, winnerIndex } = action.payload;
      const horizontalLineCount = state.config.horizontalLineCount || 15;
      const horizontalLines = generateHorizontalLines(
        state.ladderData.verticalLines,
        horizontalLineCount,
        state.config
      );

      const { path, resultIndex } = calculatePath(
        state.selectedStartIndex,
        state.ladderData.verticalLines,
        horizontalLines,
        state.config
      );

      const shuffledRewards = shuffleRewardsWithConstraint(
        rewards,
        winnerIndex,
        resultIndex
      );

      return {
        ...state,
        status: GAME_STATUS.SHUFFLING,
        rewards,
        resultIndex,
        shuffledRewards,
        rewardsRevealed: false,
        ladderData: {
          ...state.ladderData,
          horizontalLines,
          path,
        },
      };
    }

    case ACTIONS.COMPLETE_SHUFFLE: {
      return {
        ...state,
        status: GAME_STATUS.ANIMATING,
        rewardsRevealed: true,
      };
    }

    case ACTIONS.UPDATE_ANIMATION: {
      return {
        ...state,
        status: GAME_STATUS.ANIMATING,
        animationProgress: action.payload.progress,
      };
    }

    case ACTIONS.COMPLETE_ANIMATION: {
      return {
        ...state,
        status: GAME_STATUS.RESULT,
        animationProgress: 1,
        // resultIndex already set in START_GAME
      };
    }

    case ACTIONS.RESTART_GAME: {
      return {
        ...state,
        status: GAME_STATUS.SELECTING,
        selectedStartIndex: null,
        animationProgress: 0,
        resultIndex: null,
        rewards: null,
        shuffledRewards: null,
        rewardsRevealed: false,
        ladderData: {
          ...state.ladderData,
          horizontalLines: [],
          path: [],
        },
      };
    }

    default:
      return state;
  }
}

/**
 * Create initial state for ladder game
 */
export function createInitialLadderState(config) {
  return {
    status: GAME_STATUS.IDLE,
    config,
    selectedStartIndex: null,
    rewards: null,
    shuffledRewards: null,
    rewardsRevealed: false,
    ladderData: {
      verticalLines: [],
      horizontalLines: [],
      path: [],
    },
    animationProgress: 0,
    resultIndex: null,
  };
}
